import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { Brand } from '@luxuryescapes/lib-regions/lib/regions';

import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { LUX_PLUS } from '~/consts/membership';
import { ROLE_ADMIN_USER, ROLE_HOTEL_COORDINATOR } from '~/consts/roles';
import { LIST_VISIBILITY, LUX_PLUS_SCHEDULE } from '~/consts/schedule';

import { allRegions } from '../../../../consts/allRegions';
import { offerQueryKeys } from '../../../../hooks/apis/offers/useGetOffer';
import OffersService from '../../../../services/OffersService';
import { buttonMessages, buttonStates } from '../../../Common/Forms/states/submitButton';
import ScheduleTableRow from '../common/ScheduleTableRow';
import { ScheduleUpdateResult } from '../common/types';
import {
  ListVisibilityDateConflict,
  formatListVisibilityDateConflictMessage,
  getListVisibilityDateConflict,
  getRegionName,
  updateResultToErrorMessage,
} from '../common/utils';

interface Props {
  brand?: Brand;
  schedules: Array<App.Schedule>;
  types: Array<string>;
  shouldMigrateSchedule: boolean;
  onSaveFinished: () => void;
  onDelete: (id: number) => void;
  onUpdateErrors: (error: Array<string>) => void;
  offerId: string;
  isDeletingAll?: boolean;
  isDeletingLuxPlus?: boolean;
  columnLabel?: string;
  getLabel?: (schedule: App.Schedule) => string;
  filterFn?: (schedule: App.Schedule, type: string) => boolean;
  sortFn?: (a: App.Schedule, b: App.Schedule) => number;
  getRowWarning?: (schedule: App.Schedule) => string | undefined;
  onSavingChange?: (saving: boolean) => void;
}

interface ScheduleState {
  modified: boolean;
  schedule: App.Schedule;
  original: App.Schedule;
}

export interface ScheduleTableHandle {
  applyBulkDates: (start: string, end: string) => void;
}

function schedulesEqual(a: App.Schedule, b: App.Schedule): boolean {
  return (
    new Date(a.start).getTime() === new Date(b.start).getTime() &&
    new Date(a.end).getTime() === new Date(b.end).getTime() &&
    a.region === b.region &&
    a.brand === b.brand &&
    a.type === b.type &&
    !!a.restrict_purchase_to_lux_plus_members === !!b.restrict_purchase_to_lux_plus_members
  );
}

async function updateSchedules(
  scheduleStatesByType: Record<string, Array<ScheduleState>>,
  offerId: string,
  getLabel: (schedule: App.Schedule) => string,
): Promise<Array<ScheduleUpdateResult>> {
  const scheduleStatesToUpdate = Object.values(scheduleStatesByType)
    .flat()
    .filter((s) => s.modified);

  const updates = scheduleStatesToUpdate.map((s) => {
    const { id, start, end, region, restrict_purchase_to_lux_plus_members, brand, type } = s.schedule;
    const updatePayload = {
      start: new Date(start).toISOString(),
      end: new Date(end).toISOString(),
      region,
      brand,
      type,
      restrict_purchase_to_lux_plus_members,
    };
    return OffersService.updateSchedule(updatePayload, offerId, id);
  });

  const results = await Promise.allSettled(updates);

  return results.map((result, i) => ({
    label: getLabel(scheduleStatesToUpdate[i].schedule),
    result,
  }));
}

function scheduleRegionNameCompare(a: App.Schedule, b: App.Schedule) {
  return getRegionName(a.region).localeCompare(getRegionName(b.region));
}

function buildStateByType(
  schedules: Array<App.Schedule>,
  types: Array<string>,
  brand: Props['brand'],
  filterFn: Props['filterFn'],
  sortFn: Props['sortFn'],
): Record<string, Array<ScheduleState>> {
  const resolvedFilter = (s: App.Schedule, type: string) =>
    filterFn ? filterFn(s, type) : s.type === type && s.brand === brand;
  const resolvedSort = sortFn ?? scheduleRegionNameCompare;
  return types.reduce<Record<string, Array<ScheduleState>>>((acc, type) => {
    const states = schedules
      .filter((s) => resolvedFilter(s, type))
      .sort(resolvedSort)
      .map((schedule) => ({
        modified: false,
        schedule: { ...schedule },
        original: { ...schedule },
      }));
    return { ...acc, [type]: states };
  }, {});
}

function ScheduleTable(
  {
    brand,
    schedules,
    types,
    shouldMigrateSchedule,
    onSaveFinished,
    onDelete,
    onUpdateErrors,
    offerId,
    isDeletingAll = false,
    isDeletingLuxPlus = false,
    columnLabel = 'Country',
    getLabel,
    filterFn,
    sortFn,
    getRowWarning,
    onSavingChange,
  }: Props,
  ref: React.Ref<ScheduleTableHandle>,
) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [saveState, setSaveState] = useState(buttonStates.default);
  const [leScheduleStatesByType, setLeScheduleStatesByType] = useState<Record<string, Array<ScheduleState>>>(() =>
    buildStateByType(schedules, types, brand, filterFn, sortFn),
  );

  const resolvedGetLabel = useCallback(
    (s: App.Schedule) => (getLabel ? getLabel(s) : (allRegions.find((r) => r.code === s.region)?.name ?? s.region)),
    [getLabel],
  );

  const cleanStateByType = useCallback(() => {
    setLeScheduleStatesByType(buildStateByType(schedules, types, brand, filterFn, sortFn));
  }, [schedules, types, brand, filterFn, sortFn]);

  useEffect(cleanStateByType, [schedules, types, cleanStateByType]);

  const onScheduleChange = useCallback((schedule: App.Schedule, type: string) => {
    setLeScheduleStatesByType((prev) => ({
      ...prev,
      [type]: prev[type].map((s) =>
        s.schedule.id === schedule.id ? { ...s, modified: !schedulesEqual(schedule, s.original), schedule } : s,
      ),
    }));
  }, []);

  const onBulkRestrictChange = useCallback((type: string, checked: boolean) => {
    setLeScheduleStatesByType((prev) => ({
      ...prev,
      [type]: (prev[type] ?? []).map((s) => {
        const schedule = { ...s.schedule, restrict_purchase_to_lux_plus_members: checked };
        return { ...s, modified: !schedulesEqual(schedule, s.original), schedule };
      }),
    }));
  }, []);

  const applyBulkDates = useCallback((start: string, end: string) => {
    setLeScheduleStatesByType((prev) =>
      Object.fromEntries(
        Object.entries(prev).map(([type, states]) => [
          type,
          states.map((s) => {
            if (!allRegions.some((r) => r.code === s.schedule.region)) {
              return s;
            }
            const schedule = { ...s.schedule, start, end };
            return { ...s, modified: !schedulesEqual(schedule, s.original), schedule };
          }),
        ]),
      ),
    );
  }, []);

  // only used by scoped bulk-update controls rendered above this table
  useImperativeHandle(ref, () => ({ applyBulkDates }), [applyBulkDates]);

  const modifiedCount = Object.values(leScheduleStatesByType)
    .flat()
    .filter((s) => s.modified).length;
  const anyModified = modifiedCount > 0;

  const saveChanges = useCallback(async () => {
    const modifiedListVisibility = (leScheduleStatesByType[LIST_VISIBILITY] ?? []).filter((s) => s.modified);
    if (modifiedListVisibility.length > 0) {
      const conflictMessages = Array.from(
        new Set(
          modifiedListVisibility
            .map((s) => getListVisibilityDateConflict({ start: s.schedule.start, end: s.schedule.end }, schedules))
            .filter((conflict): conflict is ListVisibilityDateConflict => !!conflict)
            .map(formatListVisibilityDateConflictMessage),
        ),
      );

      if (conflictMessages.length > 0) {
        onUpdateErrors(conflictMessages);
        return;
      }
    }

    setSaveState(buttonStates.saving);
    onSavingChange?.(true);

    const updateResults = await updateSchedules(leScheduleStatesByType, offerId, resolvedGetLabel);

    // Optimistically write saved values into the React Query cache before invalidating.
    // This prevents a brief flash where schedules appear un-modified during the background refetch.
    const savedById = new Map(
      Object.values(leScheduleStatesByType)
        .flat()
        .filter((s) => s.modified)
        .map((s) => [s.schedule.id, s.schedule]),
    );
    if (savedById.size > 0) {
      queryClient.setQueryData(offerQueryKeys.detail(offerId), (old: any) => {
        if (!old) {
          return old;
        }
        return {
          ...old,
          brand_schedules: old.brand_schedules.map((s: App.Schedule) => {
            const saved = savedById.get(s.id);
            return saved ? { ...s, ...saved } : s;
          }),
        };
      });
    }

    onSaveFinished();

    if (updateResults.some((r) => r.result.status === 'rejected')) {
      const updateErrorMessages = updateResults.map(updateResultToErrorMessage).filter((m) => m);
      onUpdateErrors(updateErrorMessages);
      enqueueSnackbar('Some schedules failed to save', { variant: 'error' });
    } else {
      enqueueSnackbar('Schedules saved', { variant: 'success' });
    }

    setSaveState(buttonStates.default);
    onSavingChange?.(false);
  }, [
    leScheduleStatesByType,
    offerId,
    resolvedGetLabel,
    onUpdateErrors,
    onSaveFinished,
    enqueueSnackbar,
    queryClient,
    onSavingChange,
  ]);

  const saving = saveState === buttonStates.saving;

  return (
    <Box>
      <Stack direction="column" gap={4} alignItems="flex-start">
        {types.map((type) => {
          const leScheduleStates = leScheduleStatesByType[type] ?? [];
          const visibleRows = leScheduleStates.filter(({ schedule }) =>
            allRegions.find((r) => r.code === schedule.region),
          );
          const isLuxPlus = type === LUX_PLUS_SCHEDULE;
          const showBulkRestrict = isLuxPlus && visibleRows.length > 0;
          const colSpan = showBulkRestrict ? 4 : 3;

          const luxPlusStates = leScheduleStatesByType[LUX_PLUS_SCHEDULE] ?? [];
          const allLuxPlusRestricted =
            luxPlusStates.length > 0 && luxPlusStates.every((s) => s.schedule.restrict_purchase_to_lux_plus_members);
          const someLuxPlusRestricted = luxPlusStates.some((s) => s.schedule.restrict_purchase_to_lux_plus_members);

          return (
            <Box key={type} sx={{ overflowX: 'auto', width: '100%' }}>
              <Table size="small" sx={{ minWidth: 600 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '20%' }}>{columnLabel}</TableCell>
                    <TableCell>{isLuxPlus && `${LUX_PLUS.PROGRAM_NAME} `}Schedule</TableCell>
                    {showBulkRestrict && (
                      <TableCell sx={{ width: '260px' }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={allLuxPlusRestricted}
                              indeterminate={someLuxPlusRestricted && !allLuxPlusRestricted}
                              onChange={(e) => onBulkRestrictChange(type, e.target.checked)}
                              disabled={shouldMigrateSchedule || saving}
                            />
                          }
                          label="Restrict all"
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ width: '140px' }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={colSpan}>
                        <Typography variant="body2" color="text.secondary" py={2} textAlign="center">
                          No schedules found. Use the bulk update or add a schedule to get started.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    visibleRows.map(({ schedule, modified }) => (
                      <ScheduleTableRow
                        label={resolvedGetLabel(schedule)}
                        onChange={onScheduleChange}
                        type={type}
                        isModified={modified}
                        key={schedule.id}
                        schedule={schedule}
                        disabled={shouldMigrateSchedule || saving}
                        isDeleting={isDeletingAll || (isLuxPlus && isDeletingLuxPlus)}
                        onDelete={onDelete}
                        warning={getRowWarning?.(schedule)}
                      />
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          );
        })}
      </Stack>
      <Stack direction="row" justifyContent="flex-end" gap={1} mt={2}>
        {anyModified && (
          <Button onClick={cleanStateByType} disabled={saving}>
            Clear changes ({modifiedCount})
          </Button>
        )}
        <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_HOTEL_COORDINATOR]}>
          <Button
            variant="contained"
            onClick={saveChanges}
            disabled={!anyModified || saveState !== buttonStates.default}
          >
            {buttonMessages[saveState]}
          </Button>
        </PermissionedComponent>
      </Stack>
    </Box>
  );
}

export default forwardRef(ScheduleTable);
